import { Box, Divider as MUIDivider } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  selectIsDisabled,
  selectIsOrganizationDataAccessible,
  selectViewsRestrictions,
  ViewAccessType,
} from '@/entities/session';
import { ROUTES } from '@/shared/constants';
import { useAppSelector } from '@/shared/model';
import {
  DatasourcesIcon,
  DashboardsIcon,
  ExploreIcon,
  OverviewIcon,
  ProductsCatalogIcon,
  Features,
  useFeatureFlags,
} from '@/shared/ui';
import { OldVersionSwitcher } from '../../../VersionSwitcher';
import { BottomMenuProvider, useBottomMenuClose } from '../BottomItem';
import { ExplorePanel } from '../ExplorePanel/ExplorePanel';
import { HelpBottomItem } from '../HelpBottomItem/HelpBottomItem';
import { Logo } from '../Logo/Logo';
import { OrgBottomItem } from '../OrgBottomItem';
import { ProfileBottomItem } from '../ProfileBottomItem/ProfileBottomItem';
import { SidebarTab } from '../SidebarTab/SidebarTab';

const EXPLORE_ROUTES = [
  ROUTES.cohortMarketingPerformance,
  ROUTES.calendarInsights,
  ROUTES.cohortChart,
  ROUTES.calendarChart,
  ROUTES.cohortScanner,
  ROUTES.cohortRetention,
  ROUTES.roiBreakdown,
];

const Divider = styled(MUIDivider)(({ theme }) => ({
  width: `calc(100% - ${theme.spacing(2)})`,
}));

const DrawerRoot = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  height: '100%',
  position: 'relative',
  flexShrink: 0,
  zIndex: 1301,
}));

const SidebarRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'expanded',
})<{ expanded?: boolean }>(({ theme, expanded }) => ({
  width: 68,
  minWidth: 68,
  height: '100%',
  backgroundColor: '#ffffff',
  borderRadius: theme.shape.borderRadius * 4,
  boxShadow: '0px 2px 6px 0px rgba(50, 50, 71, 0.16)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  zIndex: 1301,
  position: 'relative',

  ...(expanded && {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    boxShadow: 'none',
  }),
}));

const NavTabs = styled(Box)(() => ({
  flexGrow: 1,
  width: '100%',
  position: 'relative',
  overflow: 'hidden',
}));

const NavTabsInner = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  width: '100%',
  height: '100%',
  alignItems: 'center',
  overflowY: 'auto',
  overflowX: 'hidden',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
}));

function NavTabsScrollable({ children }: { children: ReactNode }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  const [thumb, setThumb] = useState({ top: 0, height: 0, visible: false });
  const dragStartY = useRef<number | null>(null);
  const dragStartScrollTop = useRef<number>(0);

  const updateThumb = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ratio = el.clientHeight / el.scrollHeight;
    setThumb({
      top: (el.scrollTop / el.scrollHeight) * el.clientHeight,
      height: Math.max(ratio * el.clientHeight, 20),
      visible: ratio < 0.99,
    });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateThumb();
    el.addEventListener('scroll', updateThumb, { passive: true });
    const ro = new ResizeObserver(updateThumb);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateThumb);
      ro.disconnect();
    };
  }, [updateThumb]);

  const handleThumbMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragStartY.current = e.clientY;
    dragStartScrollTop.current = scrollRef.current?.scrollTop ?? 0;

    const onMouseMove = (ev: MouseEvent) => {
      const el = scrollRef.current;
      if (!el || dragStartY.current === null) return;
      const delta = ev.clientY - dragStartY.current;
      const scrollRatio = el.scrollHeight / el.clientHeight;
      el.scrollTop = dragStartScrollTop.current + delta * scrollRatio;
    };

    const onMouseUp = () => {
      dragStartY.current = null;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  return (
    <NavTabs
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <NavTabsInner ref={scrollRef}>{children}</NavTabsInner>
      {hovered && thumb.visible && (
        <Box
          onMouseDown={handleThumbMouseDown}
          sx={{
            position: 'absolute',
            right: 2,
            top: thumb.top,
            width: 4,
            height: thumb.height,
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: 2,
          }}
        />
      )}
    </NavTabs>
  );
}

const BottomArea = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 8,
  paddingBottom: 8,
  width: '100%',
}));

export function NavigationDrawerV2() {
  return (
    <BottomMenuProvider>
      <Content />
    </BottomMenuProvider>
  );
}

function Content() {
  const { isFeatureAvailable } = useFeatureFlags();
  const closeBottomMenu = useBottomMenuClose();
  const isUserDisabled = useAppSelector(selectIsDisabled);
  const openTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isExplorePanelVisible, setIsExplorePanelVisible] = useState(false);

  const handleExploreTabEnter = () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    if (openTimeoutRef.current) clearTimeout(openTimeoutRef.current);
    openTimeoutRef.current = setTimeout(
      () => setIsExplorePanelVisible(true),
      200,
    );
  };

  const handleExploreTabLeave = () => {
    if (openTimeoutRef.current) clearTimeout(openTimeoutRef.current);
    closeTimeoutRef.current = setTimeout(
      () => setIsExplorePanelVisible(false),
      100,
    );
  };

  const handlePanelEnter = () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
  };

  const handlePanelLeave = () => {
    closeTimeoutRef.current = setTimeout(
      () => setIsExplorePanelVisible(false),
      100,
    );
  };
  const viewsRestrictions = useAppSelector(selectViewsRestrictions);
  const isOrganizationDataAccessible = useAppSelector(
    selectIsOrganizationDataAccessible,
  );
  const { pathname } = useLocation();
  const isExploreActive = EXPLORE_ROUTES.some((r) => pathname.startsWith(r));
  const showOverview =
    isOrganizationDataAccessible &&
    viewsRestrictions?.overview !== ViewAccessType.DENIED;
  const showExplore =
    isOrganizationDataAccessible &&
    (viewsRestrictions?.cohortMarketingPerformance !== ViewAccessType.DENIED ||
      viewsRestrictions?.calendarInsights !== ViewAccessType.DENIED ||
      viewsRestrictions?.cohortChart !== ViewAccessType.DENIED ||
      viewsRestrictions?.calendarChart !== ViewAccessType.DENIED ||
      viewsRestrictions?.cohortScanner !== ViewAccessType.DENIED ||
      viewsRestrictions?.cohortRetention !== ViewAccessType.DENIED ||
      viewsRestrictions?.roiBreakdown !== ViewAccessType.DENIED);
  const showDashboards =
    isOrganizationDataAccessible &&
    viewsRestrictions?.dashboards !== ViewAccessType.DENIED;
  const showIntegrations =
    viewsRestrictions?.integrations !== ViewAccessType.DENIED;
  const showProductCatalog =
    viewsRestrictions?.productsCatalog !== ViewAccessType.DENIED;

  return (
    <DrawerRoot component="nav">
      <SidebarRoot expanded={isExplorePanelVisible} onClick={closeBottomMenu}>
        <Link to={ROUTES.home}>
          <Logo />
        </Link>

        <NavTabsScrollable>
          {showOverview && (
            <SidebarTab
              icon={<OverviewIcon />}
              label="Overview"
              active={pathname.startsWith(ROUTES.overview)}
              to={ROUTES.overview}
            />
          )}
          {showExplore && (
            <Box
              onMouseEnter={handleExploreTabEnter}
              onMouseLeave={handleExploreTabLeave}
            >
              <SidebarTab
                icon={<ExploreIcon />}
                label="Explore"
                active={isExploreActive}
              />
            </Box>
          )}
          {showDashboards && (
            <SidebarTab
              icon={<DashboardsIcon />}
              label="Dashboards"
              active={pathname.startsWith(ROUTES.dashboards)}
              to={ROUTES.dashboards}
            />
          )}
          {(showOverview || showExplore || showDashboards) && <Divider />}
          {showProductCatalog && (
            <SidebarTab
              icon={<ProductsCatalogIcon />}
              label="Product Catalog"
              active={pathname.startsWith(ROUTES.productsCatalog)}
              to={ROUTES.productsCatalog}
            />
          )}
          {showIntegrations && (
            <SidebarTab
              icon={<DatasourcesIcon />}
              label="Integrations"
              active={pathname.startsWith(ROUTES.datasources)}
              to={ROUTES.datasources}
            />
          )}
        </NavTabsScrollable>

        <BottomArea>
          {isFeatureAvailable(Features.navigationV1Switcher) && (
            <OldVersionSwitcher />
          )}
          <HelpBottomItem />
          <Divider />
          <OrgBottomItem />
          <ProfileBottomItem />
        </BottomArea>
      </SidebarRoot>

      {!isUserDisabled && showExplore && (
        <Box
          sx={{
            position: 'absolute',
            left: 68,
            top: 0,
            bottom: 0,
            width: 280,
            zIndex: 1300,
            pointerEvents: isExplorePanelVisible ? 'auto' : 'none',
          }}
          onMouseEnter={handlePanelEnter}
          onMouseLeave={handlePanelLeave}
        >
          <ExplorePanel visible={isExplorePanelVisible} />
        </Box>
      )}
    </DrawerRoot>
  );
}
